/**
 * Version Router
 * 
 * Erkennt die Projektversion und routet zur entsprechenden Logik:
 * - Version 2.0 → VFS (Multifile)
 * - Version < 2.0 oder ohne Version → alte Logik (unverändert!)
 */

import { ProjectJson, ProjectJsonV2, ProjectJsonLegacy, isProjectV2, isProjectLegacy, FileDefinition } from './types';

/**
 * Prüft ob ein Projekt das Multifile-System nutzen soll (Version 2.0)
 */
export function shouldUseVFS(project: ProjectJson | null | undefined): boolean {
  if (!project) return false;
  return isProjectV2(project);
}

/**
 * Prüft ob ein Projekt die alte Tab-Logik nutzen soll
 */
export function shouldUseLegacy(project: ProjectJson | null | undefined): boolean {
  if (!project) return true;
  return isProjectLegacy(project);
}

/**
 * Holt die Datei-Definitionen aus einem Version 2.0 Projekt
 */
export function getFilesFromProject(project: ProjectJsonV2): FileDefinition[] {
  return project.filesystem?.files || [];
}

/**
 * Konvertiert alte Tabs zu FileDefinitions (für interne Darstellung)
 * ACHTUNG: Dies ändert NICHT die Speicherlogik! Alte Projekte nutzen weiterhin LocalStorage.
 */
export function tabsToFileDefinitions(project: ProjectJsonLegacy): FileDefinition[] {
  if (!project.tabs) return [];
  
  return project.tabs.map(tab => ({
    path: tab.code || `${tab.name.toLowerCase()}.${getExtensionForLanguage(tab.language)}`,
    name: tab.name,
    language: tab.language,
    replaceKey: tab.replaceKey,
    active: tab.active
  }));
}

/**
 * Hilfsfunktion: Holt die Dateiendung für eine Sprache
 */
function getExtensionForLanguage(language: string): string {
  const map: Record<string, string> = {
    'html': 'html',
    'css': 'css',
    'javascript': 'js',
    'typescript': 'ts',
    'json': 'json',
    'python': 'py',
    'java': 'java',
    'cpp': 'cpp',
    'c': 'c'
  };
  return map[language] || 'txt';
}

/**
 * Holt alle Datei-Definitionen aus einem Projekt (unabhängig von Version)
 * Für UI-Darstellung - ändert nicht die Speicherlogik!
 */
export function getFileDefinitions(project: ProjectJson | null | undefined): FileDefinition[] {
  if (!project) return [];
  
  if (isProjectV2(project)) {
    return getFilesFromProject(project);
  }
  
  if (isProjectLegacy(project)) {
    return tabsToFileDefinitions(project);
  }
  
  return [];
}

/**
 * Findet die aktive Datei in einem Projekt
 */
export function getActiveFile(project: ProjectJson | null | undefined): FileDefinition | undefined {
  const files = getFileDefinitions(project);
  return files.find(f => f.active) || files[0];
}

